import { getUserColorClass } from '@/components/ui/UserBadge/utils';
import { cn } from '@/lib/utils';
import { ReactNode, useState } from 'react';
import { representByThree } from '@certhon/shared';

interface HiddenEventsToggleProps {
  /** Count of hidden events */
  count: number;
  /** User IDs involved in the hidden events, used for representative dots */
  userIds: (number | null)[];
  /** Content to render when expanded */
  children: ReactNode;
}

/**
 * Collapsed/expanded toggle for a group of hidden timeline events.
 * When collapsed, shows representative user-colored dots and a "N hidden events" label.
 * When expanded, renders children (the individual event entries).
 */
export function HiddenEventsToggle({
  count,
  userIds,
  children,
}: HiddenEventsToggleProps) {
  const [isExpanded, setIsExpanded] = useState(false);

  if (count === 0) {
    return null;
  }

  if (isExpanded) {
    return <>{children}</>;
  }

  const representativeUserIds = representByThree(
    userIds.filter((id): id is number => id != null),
  );

  return (
    <li className="relative flex gap-x-2">
      {/* Timeline line - continuous */}
      <div className="absolute top-0 -bottom-6 left-0 flex w-6 justify-center">
        <div className="bg-border w-px" />
      </div>

      {/* User dots */}
      <div
        className="bg-card relative flex size-6 flex-none flex-col items-center justify-center
          gap-0.5"
      >
        {representativeUserIds.map((userId, index) => (
          <div
            key={`${userId}-${index}`}
            className={cn('size-1.5 rounded-full', getUserColorClass(userId!))}
          />
        ))}
      </div>

      {/* Clickable text with horizontal break lines */}
      <div className="flex h-6 min-w-0 flex-auto items-center opacity-60">
        <button
          onClick={() => setIsExpanded(true)}
          className="flex w-full items-center gap-2 text-xs transition-colors"
        >
          <div className="border-border h-px w-6 border-t border-dashed" />
          <span className="text-muted-foreground hover:text-foreground">
            {count} hidden event{count === 1 ? '' : 's'}
          </span>
          <div className="border-border h-px flex-1 border-t border-dashed" />
        </button>
      </div>
    </li>
  );
}
